import { useQuery, useMutation, useQueryClient, requestDeals } from '@workspace/api';
import type { Contact, PaginatedResponse } from './quotes';

export interface ContactFilters {
    search?: string;
    type_personne?: string;
    deja_client?: boolean;
    date?: string;
    per_page?: number;
    page?: number;
    [key: string]: string | number | string[] | boolean | undefined;
}

/**
 * Hook pour récupérer la liste des contacts avec filtres et pagination
 */
export function useContacts(filters: ContactFilters = {}) {
    return useQuery<PaginatedResponse<Contact>>({
        queryKey: ['deals', 'contacts', filters],
        queryFn: async () => {
            const response = await requestDeals.get<PaginatedResponse<Contact>>('/api/contacts', { params: filters });
            return response.data;
        },
        staleTime: 30 * 1000, // 30 secondes
    });
}

/**
 * Hook pour récupérer un contact unique par son ID
 */
export function useContact(id: string | number) {
    return useQuery<{ data: Contact }>({
        queryKey: ['deals', 'contacts', id],
        queryFn: async () => {
            const response = await requestDeals.get<{ data: Contact }>(`/api/contacts/${id}`);
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: !!id,
    });
}

/**
 * Hook pour récupérer les devis d'un contact
 */
export function useContactQuotes(contactId: string | number) {
    return useQuery<PaginatedResponse<import('./quotes').Quote>>({
        queryKey: ['deals', 'contacts', contactId, 'quotes'],
        queryFn: async () => {
            const response = await requestDeals.get<PaginatedResponse<import('./quotes').Quote>>(`/api/contacts/${contactId}/quotes`, {
                params: { per_page: 100 },
            });
            return response.data;
        },
        staleTime: 30 * 1000, // 30 secondes
        enabled: !!contactId,
    });
}

export interface EmailConsent {
    status: boolean;
    consent_at: string | null;
    source: string | null;
    // Link n'a aucun statut pour cette finalité : autorisé depuis la création du contact Link (consent_at)
    is_default: boolean;
}

export interface EmailConsents {
    email_relational: EmailConsent;
    email_promotional: EmailConsent;
}

/**
 * Hook pour récupérer les consentements email (relationnel / promotionnel) d'un contact, tenus par Link.
 * `data` vaut null quand le contact n'a pas d'email ou que Link est indisponible.
 */
export function useContactEmailConsents(contactId: string | number) {
    return useQuery<{ data: EmailConsents | null }>({
        queryKey: ['deals', 'contacts', contactId, 'email-consents'],
        queryFn: async () => {
            const response = await requestDeals.get<{ data: EmailConsents | null }>(`/api/contacts/${contactId}/email-consents`);
            return response.data;
        },
        staleTime: 30 * 1000,
        enabled: !!contactId,
    });
}

/**
 * Hook pour supprimer un contact
 */
export function useDeleteContact() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            const response = await requestDeals.delete(`/api/contacts/${id}`);
            return response.data;
        },
        onSuccess: (_, id) => {
            // Retirer immédiatement le contact du cache
            queryClient.removeQueries({ queryKey: ['deals', 'contacts', id] });
            // Invalider toutes les listes de contacts
            queryClient.invalidateQueries({ queryKey: ['deals', 'contacts'] });
        },
    });
}
